<template>
  <button
    :class="{
      loading: isLoading && !toggle,
      disabled,
      'icon-only': !action,
      'icon-right': iconRight,
      'full-width': fullWidth,
      fault: warning,
      small,
      toggle,
      rounded,
      active: isActive && toggle,
      center
    }"
    :disabled="disabled"
    class="btn"
    @click="isActive = !isActive"
  >
    <slot v-if="!iconRight" />
    <span v-if="action">{{ action }}</span>
    <slot v-if="iconRight" />
    <slot name="custom-toggle">
      <ck-icon
        v-if="toggle && !isActive && !isLoading"
        name="ico-arrow-forward-16px"
        fill="grey-dark"
      />
      <ck-icon
        v-if="toggle && isActive"
        name="ico-arrow-down-16px"
        fill="white"
      />
    </slot>
    <div v-if="isLoading && !toggle" class="lds-ring">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  </button>
</template>

<script>
export default {
  props: {
    fullWidth: {
      type: Boolean,
      default: true
    },
    action: String,
    disabled: Boolean,
    isLoading: Boolean,
    iconRight: Boolean,
    warning: Boolean,
    small: Boolean,
    toggle: Boolean,
    rounded: Boolean,
    center: Boolean
  },
  data() {
    return {
      isActive: false
    }
  }
}
</script>

<style lang="less" scoped>
@import (reference) '~@/assets/less/imports/importer.less';

@button-primary-text: @white;
@button-primary-bg: @blue-mid;
@button-primary-border: @blue-mid;
@button-primary-bg-hover: @blue-mid;
@button-primary-border-hover: @blue-mid;
@button-tertiary-text: @blue-middark;
@button-tertiary-bg: @white;
@button-tertiary-border: transparent;
@button-tertiary-bg-hover: @blue-extralight;
@button-tertiary-border-hover: transparent;
@button-secondary-text: @blue-middark;
@button-secondary-bg: @white;
@button-secondary-border: fade(@black, 4);
@button-secondary-bg-hover: fade(@blue-extralight, 30);
@button-secondary-border-hover: @blue-middark;
@button-secondary-disabled-text: fade(@blue-middark, 30);
@button-secondary-disabled-border: @blue-extralight;
@button-secondary-disabled-bg: fade(@blue-extralight, 30);

@button-active-text: @white;
@button-active-bg: @purple-midlight;
@button-active-border: @purple-midlight;
@button-active-bg-hover: @purple-midlight;
@button-active-border-hover: @purple-midlight;
@button-disabled-text: @grey-midlight;
@button-disabled-bg: @grey-extralight;
@button-disabled-border: @grey-extralight;
@button-disabled-bg-hover: @grey-extralight;
@button-disabled-border-hover: @grey-extralight;
@button-fault-text: @red-mid;
@button-fault-bg: @white;
@button-fault-border: fade(@black, 4);
@button-fault-bg-hover: fade(@red-light, 30);
@button-fault-border-hover: transparent;

@button-toggle-text: @white;
@button-toggle-bg: fade(@grey-dark, 20);
@button-toggle-border: transparent;
@button-toggle-text-hover: @grey-dark;
@button-toggle-bg-hover: fade(@grey-dark, 10);
@button-toggle-bg-active: @white;
@button-toggle-border-hover: transparent;

@btn-border-radius: @border-radius-xs;
@btn-small-border-radius: @border-radius-s;
@btn-height: 52px;
@btn-medium-height: 36px;
@btn-small-height: 40px;
@btn-extra-small-height: 24px;
@btn-extra-small-border-radius: 2px;
@btn-padding-large: 17px;
@btn-padding-small: @space-s;
@btn-small-padding-large: 17px;
@btn-extra-small-padding-large: 10px;
@btn-extra-small-padding-small: 5px;
@btn-padding-icon: 7px;
@btn-small-padding-icon: 12px;
@btn-extra-small-padding-icon: 5px;
@btn-loader-size: 36px;

.btn {
  .lh-display(flex);
  .lh-align-items(center);
  .lh-justify-content(center);
  .lh-transition(all 0.3s cubic-bezier(0, 0.5, 0.5, 1));
  .fontsize-s;
  .font-semibold;
  .pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  height: @btn-height;
  border-radius: @btn-border-radius;
  padding: 0 @btn-padding-small;
  position: relative;
  text-transform: uppercase;

  // Default: primary
  .btn.primary;

  ::v-deep .ico {
    .size(32px);
  }

  ::v-deep > span {
    white-space: nowrap;

    &:not(.ico) {
      padding-left: @btn-padding-icon;
    }
  }

  span:only-child {
    padding-left: 0;
    text-align: center;
  }

  &.icon-only {
    ::v-deep .ico {
      margin-left: 0;
      margin-right: 0;
      padding: 0;
    }

    &.small {
      padding: @btn-small-padding-small;
      height: auto;
      border-radius: @btn-small-border-radius;
    }

    &.secondary {
      padding: 0 @btn-small-padding-small;
    }
  }

  &.icon-right {
    .lh-justify-content(space-between);
  }

  &.inline {
    .lh-display(inline-flex);

    &:not(:only-child):not(:last-child) {
      margin-right: 8px;
    }
  }

  &.full-width {
    width: 100%;
  }

  &.loading {
    padding-right: @btn-loader-size + @btn-padding-large;
  }

  &.center {
    margin: 0 auto;
  }
}

// Extra classes
.btn.primary {
  .shadow;
  .gradient-blue;
  color: @button-primary-text;

  &:not(.full-width):not(.toggle):not(.small) {
    height: @btn-medium-height;
  }

  &:not(.disabled):not(.secondary):not(.tertiary):not(.proceed):not(.extra-small.rounded.center) {
    &:hover {
      .gradient-blue-pressed;
    }
  }

  &.disabled,
  &.disabled:not(.secondary):not(.tertiary):not(.proceed):hover {
    .gradient-blue(fade(#099bce, 12), fade(@blue-light, 12));
    .no-shadow;
  }

  ::v-deep > span.ico {
    .fill(@white);
  }
}

.btn.active {
  color: @button-active-text;
  background-color: @button-active-bg;
  border: 1px solid @button-active-border;

  &:hover,
  &:not(.disabled):hover {
    background-color: @button-active-bg-hover;
    border: 1px solid @button-active-border-hover;
  }

  ::v-deep > span.ico {
    .fill(@white);
  }
}

.btn.secondary {
  // .btn.small;
  color: @button-secondary-text;
  background: none;
  background-color: @button-secondary-bg;
  border: 1px solid @button-secondary-border;

  &:hover {
    background-color: @button-secondary-bg-hover;
    border: 1px solid @button-secondary-border-hover;
  }

  &.disabled,
  &.disabled:hover {
    background: none;
    color: @button-secondary-disabled-text;
    background-color: @button-secondary-disabled-bg;
    border: 1px solid @button-secondary-disabled-border;
  }

  ::v-deep > span.ico {
    .fill;
  }
}

.btn.tertiary {
  color: @button-tertiary-text;
  background-color: @button-tertiary-bg;
  border: 1px solid @button-tertiary-border;
  .no-shadow;

  &,
  &.disabled,
  &.disabled:hover {
    background: none;
  }

  &.fault {
    color: @button-fault-text;
    background: none;
    border: none;
  }

  &:hover,
  &:not(.disabled):hover {
    background-color: @button-tertiary-bg-hover;
    border: 1px solid @button-tertiary-border-hover;
  }

  ::v-deep > span.ico {
    .fill;
  }
}

.btn.disabled {
  &,
  span {
    cursor: default;
  }
}

.btn.proceed {
  .shadow(0, -3px, 8px, 5, @grey-dark);
  .fontsize-m;
  background: @btn-proceed-bg;
  color: @btn-proceed-text;
  border-radius: 0;
  height: 66px;

  &.disabled,
  &.disabled:hover {
    color: @btn-proceed-disabled-text;
  }
}

.btn.fault {
  color: @button-fault-text;
  background-color: @button-fault-bg;
  border: 1px solid @button-fault-border;

  &:hover,
  &:not(.disabled):hover {
    background-color: @button-fault-bg-hover;
    border: 1px solid @button-fault-border-hover;
  }

  ::v-deep > span.ico {
    .fill(@button-fault-text);
  }
}

.btn.rounded {
  &,
  &.icon-only {
    border-radius: @btn-height;

    &.small {
      .no-shadow;
      background: fade(@white, 30);
      border-color: transparent;
      width: @btn-small-height;
      height: @btn-small-height;

      &:hover,
      &:not(.disabled):hover {
        background: fade(@white, 40);
      }

      ::v-deep .ico {
        width: @btn-extra-small-height;
        height: @btn-extra-small-height;
      }
    }
  }
}

.btn.small {
  height: @btn-small-height;
}

.btn.extra-small {
  height: @btn-extra-small-height;
}

.btn.toggle {
  .fontsize-s;
  .font-semibold;
  .btn.rounded;
  .btn.extra-small;
  .no-shadow;
  color: @button-toggle-text;
  background: @button-toggle-bg;
  border: 1px solid @button-toggle-border;
  padding: 0 @space-xs 0 @space-xs;
  text-transform: unset;
  margin-top: -1px;

  &:hover,
  &:not(.disabled):hover {
    background-color: @button-toggle-bg-hover;
  }

  &.active {
    color: @button-toggle-text-hover;
    background-color: @button-toggle-bg-active;
    border-color: @button-toggle-border-hover;

    ::v-deep .ico {
      .fill(@button-toggle-text-hover);
    }
  }

  ::v-deep .ico {
    width: 16px;
    height: 16px;
    margin-left: @space-xs;
  }
}

.btn.rounded.extra-small.center {
  .fontsize-s;
  .font-semibold;
  .btn.rounded !important;
  .btn.extra-small !important;
  .no-shadow;
  color: @button-toggle-text;
  background: @button-toggle-bg;
  border: 1px solid @button-toggle-border;
  padding: 0 @space-xs 0 @space-xs !important;
  text-transform: unset;

  &:hover,
  &:not(.disabled):hover {
    background-color: @button-toggle-bg-hover;
  }

  &:focus {
    color: @button-toggle-text-hover;
    background-color: @button-toggle-bg-active;
    border-color: @button-toggle-border-hover;

    ::v-deep .ico {
      .fill(@button-toggle-text-hover);
    }
  }

  ::v-deep .ico {
    width: 16px;
    height: 16px;
    margin-left: @space-xs;
    margin-top: -1px;
  }
}

// Specific for other components
.input-group.inline ~ .btn.inline {
  vertical-align: bottom;
  margin-right: 0;
}

div.color ~ .btn.proceed {
  margin-top: @space-xs;
}

// Loader
.btn {
  .lds-ring {
    display: inline-block;
    position: absolute;
    width: @btn-loader-size;
    height: @btn-loader-size;
    right: 10px;
    top: auto;
    bottom: auto;
  }
  .lds-ring div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 22px;
    height: 22px;
    margin: 6px;
    border: 3px solid #fff;
    border-radius: 50%;
    animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #fff transparent transparent transparent;
  }
  .lds-ring div:nth-child(1) {
    animation-delay: -0.45s;
  }
  .lds-ring div:nth-child(2) {
    animation-delay: -0.3s;
  }
  .lds-ring div:nth-child(3) {
    animation-delay: -0.15s;
  }
  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}
</style>
